/**
 * State hook for managing registry sidecar.
 * Handles sidecar width, visibility, and responsive breakpoints.
 * Persists state to localStorage.
 */

import { useCallback, useEffect, useState } from "react";

interface RegistrySidecarState {
  sidecarWidth: number;
  isSidecarOpen: boolean;
}

const STORAGE_KEY = "invoices-frontend-registry-sidecar";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 180;
const MAX_WIDTH = 320;
const MOBILE_BREAKPOINT = 1024; // lg breakpoint

/**
 * Hook for managing registry sidecar state.
 * Handles width, visibility, and localStorage persistence.
 *
 * @example
 * const {
 *   sidecarWidth,
 *   isSidecarOpen,
 *   isMobile,
 *   setSidecarWidth,
 *   toggleSidecar,
 *   MIN_WIDTH,
 *   MAX_WIDTH,
 * } = useRegistrySidecarState();
 */
export function useRegistrySidecarState() {
  const [state, setState] = useState<RegistrySidecarState>(() => {
    if (typeof window === "undefined") {
      return { sidecarWidth: DEFAULT_WIDTH, isSidecarOpen: true };
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Partial<RegistrySidecarState>;
        return {
          sidecarWidth: parsed.sidecarWidth ?? DEFAULT_WIDTH,
          isSidecarOpen: parsed.isSidecarOpen ?? true,
        };
      } catch {
        // Invalid JSON, use defaults
      }
    }
    return { sidecarWidth: DEFAULT_WIDTH, isSidecarOpen: true };
  });

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );

  // Handle window resize for mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        sidecarWidth: state.sidecarWidth,
        isSidecarOpen: state.isSidecarOpen,
      }),
    );
  }, [state.sidecarWidth, state.isSidecarOpen]);

  const setSidecarWidth = useCallback((width: number) => {
    setState((prev) => ({
      ...prev,
      sidecarWidth: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width)),
    }));
  }, []);

  const toggleSidecar = useCallback(() => {
    setState((prev) => ({ ...prev, isSidecarOpen: !prev.isSidecarOpen }));
  }, []);

  const openSidecar = useCallback(() => {
    setState((prev) => ({ ...prev, isSidecarOpen: true }));
  }, []);

  const closeSidecar = useCallback(() => {
    setState((prev) => ({ ...prev, isSidecarOpen: false }));
  }, []);

  return {
    ...state,
    isMobile,
    setSidecarWidth,
    toggleSidecar,
    openSidecar,
    closeSidecar,
    MIN_WIDTH,
    MAX_WIDTH,
    DEFAULT_WIDTH,
  };
}
